import React from "react";

/**
 * Admin-area error boundary. Catches any rendering crash in a child component
 * tree (e.g. a malformed legacy DB row) and shows a clear recoverable message
 * instead of the dreaded white blank screen.
 */
export default class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AdminErrorBoundary] crash:", error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="admin-error-boundary">
        <div className="max-w-xl w-full border border-[#E5DDD3] bg-white rounded-lg p-6 md:p-8 shadow-sm">
          <p className="admin-eyebrow text-[#B5251A]">Something went wrong</p>
          <h2 className="admin-h2 mt-1">The admin couldn’t render this section.</h2>
          <p className="text-sm text-[#2A2520] mt-3">
            Your changes are safe — this is a display error, not a data error. Please reload the page or
            head back to the dashboard. If it keeps happening on the same item, the data on that record
            may need a small clean-up.
          </p>
          <pre className="mt-4 p-3 bg-[#FAF6F0] border border-[#E5DDD3] rounded text-xs text-[#897F71] overflow-auto max-h-40 whitespace-pre-wrap" data-testid="admin-error-detail">{msg}</pre>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => { this.reset(); window.location.reload(); }}
              className="admin-btn"
              data-testid="admin-error-reload"
            >
              Reload page
            </button>
            <a href="/admin" className="admin-btn ghost" onClick={this.reset} data-testid="admin-error-home">
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
